import React, { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { LoaderCircle } from 'lucide-react'
import * as yup from 'yup'
import { Formik, Form } from 'formik'
import { useMutation } from '@apollo/react-hooks'
import { useTranslation, Trans } from 'react-i18next'
import i18next from 'i18next'
import { Turnstile } from '@marsidev/react-turnstile'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

import { Mutation } from '../../api/gql'
import Input from '../../components/form/FormikInput'
import logo from '../../assets/img/logo_signin.png'
import LanguageSelect from '../../components/LanguageSelect'
import { legalLinks } from '../../constants/legal-links.ts'

const LINK_CLASS = 'text-primary underline-offset-4 hover:underline'

const SignUp = () => {
  const { t, i18n } = useTranslation()
  const legal = legalLinks(i18n.language)
  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t('Sign_up_form_validation_email_format'))
      .required(t('Sign_up_form_validation_email_required')),
    name: yup
      .string()
      .min(3, t('Sign_up_form_validation_name_min'))
      .required(t('Sign_up_form_validation_name_required')),
    organizationName: yup
      .string()
      .min(3, t('Sign_up_form_validation_organization_name_min'))
      .required(t('Sign_up_form_validation_organization_name_required')),
    agreement: yup
      .bool()
      .oneOf([true], t('Sign_up_form_validation_agreement_required', 'required')),
  })

  const [signUp] = useMutation(Mutation.SIGN_UP)
  const [formError, setFormError] = useState(null)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState(null)
  const turnstileRef = useRef(null)

  return (
    <>
      <div className="flex items-center gap-3 self-center">
        <img src={logo} className="h-36" alt="Urkla" />
      </div>

      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t('Sign_up_registration_heading')}</CardTitle>
          <CardDescription>{t('Sign_up_registration_subheading')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Formik
            initialValues={{
              email: '',
              name: '',
              organizationName: '',
              agreement: false,
            }}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              setFormError(null)
              if (!turnstileToken) {
                setFormError(t('Sign_up_form_validation_captcha_required', 'Please complete the captcha'))
                return
              }
              try {
                await signUp({ variables: { language: i18next.language, turnstileToken, ...values } })
                setFormSubmitted(true)
              } catch (e) {
                setFormError(e.message)
                // Reset turnstile on error so user can retry
                turnstileRef.current?.reset()
                setTurnstileToken(null)
              }
            }}
          >
            {({ isSubmitting, values }) =>
              formSubmitted ? (
                <p className="text-center text-sm">
                  <Trans i18nKey="Sign_up_form_email_sent_to" email={values.email}>
                    An email was sent to <strong>{{ email: values.email }}</strong>.
                  </Trans>
                </p>
              ) : (
                <Form>
                  <div className="flex flex-col gap-4">
                    <Input
                      label={t('Sign_up_form_name_label')}
                      type="text"
                      name="name"
                      placeholder={t('Sign_up_form_name_placeholder')}
                    />
                    <Input
                      label={t('Sign_up_form_email_label')}
                      type="email"
                      name="email"
                      placeholder={t('Sign_up_form_email_placeholder')}
                    />
                    <Input
                      label={t('Sign_up_form_organization_name_label')}
                      type="text"
                      name="organizationName"
                      placeholder={t('Sign_up_form_organization_name_placeholder')}
                    />
                    <Input
                      label={
                        <Trans i18nKey="Sign_up_form_agreement">
                          I have read and agree with
                          <a href={legal.terms} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
                            Terms of Use
                          </a>{' '}
                          and
                          <a href={legal.privacy} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
                            Privacy Policy
                          </a>
                          .
                        </Trans>
                      }
                      type="checkbox"
                      name="agreement"
                    />

                    <div className="flex justify-center">
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                        onSuccess={setTurnstileToken}
                        onExpire={() => setTurnstileToken(null)}
                        options={{
                          theme: 'light',
                        }}
                      />
                    </div>

                    {formError && (
                      <div className="rounded-md bg-destructive/15 px-4 py-3 text-sm text-destructive">
                        {formError}
                      </div>
                    )}

                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting && <LoaderCircle className="animate-spin" />}
                      {t('Sign_up_form_sign_up_btn_label')}
                    </Button>

                    <p className="text-center text-sm text-muted-foreground">
                      <Link to="/auth/signin" className={LINK_CLASS}>
                        {t('Sign_up_form_sign_in_link')}
                      </Link>
                    </p>
                  </div>
                </Form>
              )
            }
          </Formik>
        </CardContent>
      </Card>

      <div className="flex justify-center">
        <LanguageSelect />
      </div>
    </>
  )
}

export default SignUp
